/**
 * Track T8 — palette CSV + printable palette report.
 *
 * The CSV now carries the FULL per-layer MOPA record (see MopaLayerRecord in
 * the pinned contract) so the legend is a self-contained, reproducible burn
 * table that travels alongside every export — native project formats aren't
 * interoperable, so this table is the portable source of truth. Each row spans:
 * run order, layer id, name, target color hex + RGB, pixel count/percent,
 * palette error, then the laser parameters (power %, speed mm/s, frequency kHz,
 * pulse width ns, hatch spacing mm, hatch angle °, hatch type, passes, focus
 * offset mm). An explicit units line and machine/material context head the file.
 */
import type {
  CutParams,
  MopaLayerRecord,
  PaletteMode,
  PhotoPaletteEntry,
  PhotoResult,
} from "../core/types";
import { APP_NAME, APP_VERSION } from "../core/types";
import { fmt } from "../core/geometry";

const PALETTE_MODE_LABEL: Record<PaletteMode, string> = {
  adaptive: "Adaptive photo colors",
  lightburn: "LightBurn exact layer colors",
};

function c2(n: number): string {
  const i = Math.trunc(Number.isFinite(n) ? n : 0);
  const body = Math.abs(i).toString().padStart(2, "0");
  return i < 0 ? "-" + body : body;
}

function stripHash(hex: string): string {
  return hex.charAt(0) === "#" ? hex.slice(1) : hex;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = stripHash(hex);
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return [Number.isFinite(r) ? r : 0, Number.isFinite(g) ? g : 0, Number.isFinite(b) ? b : 0];
}

/** CSV quote: wrap in double quotes, double any embedded quotes (matches v1.5). */
function q(value: string | number): string {
  return `"${String(value).replace(/"/g, '""')}"`;
}

function groupId(e: PhotoPaletteEntry): string {
  return `C${c2(e.layerIndex)}_photo_${stripHash(e.svgColor)}_target_${stripHash(e.targetColor)}_dots`;
}

function num(n: number): number {
  return Number.isFinite(n) ? n : 0;
}

function hasCutParams(cutParams?: Record<number, CutParams>): boolean {
  return !!cutParams && Object.keys(cutParams).length > 0;
}

/**
 * Map one palette entry (+ its optional CutParams) to the complete portable
 * MopaLayerRecord. Fields not carried by CutParams get spec-sensible defaults:
 * a solid color fill is a line hatch at 0° with a nominal focus offset of 0.
 * `runOrder` is 1-based, in palette order (the order layers are burned).
 *
 * NOTE (seam): T6 owns lbrnExport and could expose a canonical
 * CutParams->MopaLayerRecord mapping; none exists there today, so this local
 * builder is the single source of the legend record and is reused by the CSV,
 * the SVG metadata (svgExport), and the "Export all" bundle (zipWriter).
 */
export function toMopaLayerRecord(
  entry: PhotoPaletteEntry,
  runOrder: number,
  cp?: CutParams,
): MopaLayerRecord {
  return {
    index: Math.trunc(num(entry.layerIndex)),
    runOrder,
    name: `C${c2(entry.layerIndex)}`,
    targetColorHex: entry.targetColor,
    power: cp ? num(cp.power) : 0,
    speed: cp ? num(cp.speed) : 0,
    frequencyKhz: cp ? num(cp.frequencyKhz) : 0,
    pulseWidthNs: cp ? num(cp.qPulseNs) : 0,
    hatchSpacingMm: cp ? num(cp.intervalMm) : 0,
    hatchAngleDeg: 0,
    hatchType: "line",
    passes: cp ? Math.max(1, Math.round(num(cp.passes))) : 1,
    focusOffsetMm: 0,
  };
}

/** Ordered MopaLayerRecord list for a result (palette order = run order). */
export function buildMopaLegend(
  result: PhotoResult,
  cutParams?: Record<number, CutParams>,
): MopaLayerRecord[] {
  return result.palette.map((e, i) => toMopaLayerRecord(e, i + 1, cutParams?.[e.layerIndex]));
}

// Column order matches the MopaLayerRecord contract, prefixed with the
// diagnostic SVG/target-color columns the palette carries.
const CSV_HEADER = [
  "Run Order",
  "Layer",
  "Name",
  "SVG Group",
  "SVG Hex Color",
  "SVG RGB",
  "Target Color Hex",
  "Target RGB",
  "Pixel Count",
  "Percent",
  "Palette Error",
  "Power %",
  "Speed mm/s",
  "Frequency kHz",
  "Pulse Width ns",
  "Hatch Spacing mm",
  "Hatch Angle deg",
  "Hatch Type",
  "Passes",
  "Focus Offset mm",
].join(",");

/**
 * Full per-layer MOPA legend as CSV. Every row is a complete MopaLayerRecord
 * (plus SVG/target diagnostic columns). The file opens with an explicit units
 * line and machine/material context so the table is self-describing.
 */
export function exportPaletteCsv(
  result: PhotoResult,
  cutParams?: Record<number, CutParams>,
  context?: { machine?: string; material?: string; finish?: string },
): string {
  const grid = `${result.cols}x${result.rows}`;
  const physical = `${fmt(result.widthMm)} x ${fmt(result.heightMm)} mm`;
  const modeLabel = PALETTE_MODE_LABEL[result.paletteMode] ?? String(result.paletteMode);

  // Context / units preamble. Each line is a single quoted cell (# prefix marks
  // it as metadata) so spreadsheets keep the header row aligned with the table.
  const preamble: string[] = [
    `# ${APP_NAME} v${APP_VERSION} — MOPA color-layer legend`,
    `# Units: power = %, speed = mm/s, frequency = kHz, pulse width = ns, hatch spacing = mm, hatch angle = degrees, focus offset = mm. All geometry in millimeters.`,
    `# Machine: ${context?.machine || "not specified"}`,
    `# Material: ${context?.material || "not specified"}`,
    `# Finish: ${context?.finish || "not specified"}`,
    `# Palette mode: ${modeLabel}`,
    `# Source image: ${result.sourceName}`,
    `# Output grid: ${grid}`,
    `# Physical size: ${physical}`,
    `# MOPA colors are machine/material/focus specific — calibrate with a test grid before a full run.`,
  ].map((line) => q(line));

  const lines: string[] = [...preamble, CSV_HEADER];
  const legend = buildMopaLegend(result, cutParams);

  for (let i = 0; i < result.palette.length; i++) {
    const e = result.palette[i];
    const rec = legend[i];
    const [sr, sg, sb] = hexToRgb(e.svgColor);
    const [tr, tg, tb] = hexToRgb(rec.targetColorHex);
    const values: Array<string | number> = [
      rec.runOrder,
      rec.name,
      rec.name,
      groupId(e),
      e.svgColor,
      `${sr} ${sg} ${sb}`,
      rec.targetColorHex,
      `${tr} ${tg} ${tb}`,
      String(e.pixelCount),
      e.percent.toFixed(4),
      e.paletteError.toFixed(3),
      fmt(rec.power),
      fmt(rec.speed),
      fmt(rec.frequencyKhz),
      fmt(rec.pulseWidthNs),
      fmt(rec.hatchSpacingMm),
      fmt(rec.hatchAngleDeg),
      rec.hatchType,
      String(rec.passes),
      fmt(rec.focusOffsetMm),
    ];
    lines.push(values.map(q).join(","));
  }

  return lines.join("\n") + "\n";
}

// ---------- printable palette report ----------

function escHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Only accept a plain #RRGGBB color for an inline style; otherwise transparent. */
function safeSwatch(hex: string): string {
  return /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : "transparent";
}

const REPORT_CSS = `
:root { color-scheme: light; }
* { box-sizing: border-box; }
body { margin: 0; padding: 24px; font: 14px/1.5 "Segoe UI", system-ui, -apple-system, Arial, sans-serif; color: #111; background: #fff; }
header { border-bottom: 2px solid #111; padding-bottom: 12px; margin-bottom: 16px; }
h1 { margin: 0 0 4px; font-size: 22px; }
h1 .version { font-size: 13px; font-weight: 500; color: #666; margin-left: 6px; }
.meta { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 4px 24px; font-size: 13px; }
.meta .label { display: inline-block; min-width: 96px; color: #666; font-weight: 600; }
.meta .fill-line { letter-spacing: 1px; color: #999; }
.meta .notes { grid-column: 1 / -1; color: #444; }
table { width: 100%; border-collapse: collapse; margin: 8px 0 20px; font-size: 13px; }
th, td { border: 1px solid #cfcfcf; padding: 6px 8px; text-align: left; vertical-align: middle; }
thead th { background: #f2f2f2; font-weight: 700; }
td.num { text-align: right; font-variant-numeric: tabular-nums; }
td.code { font-weight: 700; white-space: nowrap; }
.mono { font-family: "Consolas", "SFMono-Regular", ui-monospace, monospace; }
.swatch { display: inline-block; width: 22px; height: 22px; border: 1px solid #999; border-radius: 3px; vertical-align: middle; }
td.actual { min-width: 130px; }
tbody tr:nth-child(even) td { background: #fafafa; }
footer { margin-top: 12px; border-top: 1px solid #ddd; padding-top: 12px; }
footer h2 { font-size: 15px; margin: 0 0 6px; }
.checklist { margin: 0 0 10px; padding-left: 20px; }
.checklist li { margin: 2px 0; }
.calibration { color: #444; font-size: 13px; margin: 6px 0 0; }
@media print {
  body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; }
  .swatch { border-color: #666; }
}
`;

/**
 * Printable HTML palette report: swatch chips, layer codes, full laser
 * parameters, machine/material header, blank "actual result" column, and a
 * surface-prep checklist. Fully self-contained (inline CSS, no external
 * assets); opens via a Blob URL. Neutral copy only.
 */
export function buildPaletteReportHtml(
  result: PhotoResult,
  meta: { machine: string; material: string; notes?: string },
  cutParams?: Record<number, CutParams>,
): string {
  const withCut = hasCutParams(cutParams);
  const modeLabel = PALETTE_MODE_LABEL[result.paletteMode] ?? String(result.paletteMode);

  const headCells: string[] = [
    "<th>Color</th>",
    "<th>Layer</th>",
    "<th>SVG hex</th>",
    "<th>Target hex</th>",
    '<th class="num">Pixels</th>',
    '<th class="num">%</th>',
  ];
  if (withCut) {
    headCells.push(
      '<th class="num">Power %</th>',
      '<th class="num">Speed mm/s</th>',
      '<th class="num">Freq kHz</th>',
      '<th class="num">Q-Pulse ns</th>',
      '<th class="num">Interval mm</th>',
      '<th class="num">Passes</th>',
    );
  }
  headCells.push("<th>Actual result</th>");

  const rows = result.palette
    .map((e) => {
      const cells: string[] = [
        `<td><span class="swatch" style="background:${safeSwatch(e.svgColor)}"></span></td>`,
        `<td class="code">C${c2(e.layerIndex)}</td>`,
        `<td class="mono">${escHtml(e.svgColor)}</td>`,
        `<td class="mono">${escHtml(e.targetColor)}</td>`,
        `<td class="num">${e.pixelCount}</td>`,
        `<td class="num">${e.percent.toFixed(1)}%</td>`,
      ];
      if (withCut) {
        const cp = cutParams ? cutParams[e.layerIndex] : undefined;
        if (cp) {
          cells.push(
            `<td class="num">${fmt(cp.power)}</td>`,
            `<td class="num">${fmt(cp.speed)}</td>`,
            `<td class="num">${fmt(cp.frequencyKhz)}</td>`,
            `<td class="num">${fmt(cp.qPulseNs)}</td>`,
            `<td class="num">${fmt(cp.intervalMm)}</td>`,
            `<td class="num">${fmt(cp.passes)}</td>`,
          );
        } else {
          cells.push('<td class="num"></td>', '<td class="num"></td>', '<td class="num"></td>', '<td class="num"></td>', '<td class="num"></td>', '<td class="num"></td>');
        }
      }
      cells.push('<td class="actual"></td>');
      return `      <tr>${cells.join("")}</tr>`;
    })
    .join("\n");

  const notesRow = meta.notes
    ? `\n      <div class="notes"><span class="label">Notes</span> ${escHtml(meta.notes)}</div>`
    : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escHtml(APP_NAME)} — Palette Report</title>
<style>${REPORT_CSS}</style>
</head>
<body>
  <header>
    <h1>${escHtml(APP_NAME)} <span class="version">v${escHtml(APP_VERSION)}</span></h1>
    <div class="meta">
      <div><span class="label">Machine</span> ${escHtml(meta.machine)}</div>
      <div><span class="label">Material</span> ${escHtml(meta.material)}</div>
      <div><span class="label">Date</span> <span class="fill-line">____________________</span></div>
      <div><span class="label">Palette mode</span> ${escHtml(modeLabel)}</div>
      <div><span class="label">Source</span> ${escHtml(result.sourceName)}</div>
      <div><span class="label">Grid</span> ${result.cols}x${result.rows}</div>
      <div><span class="label">Physical size</span> ${escHtml(fmt(result.widthMm))} x ${escHtml(fmt(result.heightMm))} mm</div>${notesRow}
    </div>
  </header>
  <table>
    <thead>
      <tr>${headCells.join("")}</tr>
    </thead>
    <tbody>
${rows}
    </tbody>
  </table>
  <footer>
    <h2>Before you run</h2>
    <ul class="checklist">
      <li>Clean the surface with 90%+ isopropyl alcohol and let it fully flash off.</li>
      <li>Handle with nitrile gloves from here on — skin oils change how the oxide colors form.</li>
      <li>Refocus for this exact part height before marking.</li>
    </ul>
    <p class="calibration">MOPA colors depend on your machine, material, and focus. Run a parameter test grid first and record the winning settings in the "Actual result" column above before committing to a full run.</p>
  </footer>
</body>
</html>`;
}
